import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Alert,
  Paper,
} from '@mui/material';
import { isDevLoginMode } from './config/env';
import { setDevCea } from './super-picker/services/auth';
import {
  api,
  buildUnauthorizedUrl,
  getDefaultAppPath,
  normalizeDevLoginApp,
  normalizeDevLoginRedirect,
  resolveProtectedAppFromPath,
  type DevLoginApp,
} from './super-picker/services/api';

export function DevLoginPage() {
  const [cea, setCea] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { app, redirect } = useMemo((): { app: DevLoginApp; redirect: string } => {
    const params = new URLSearchParams(window.location.search);
    const requestedApp = normalizeDevLoginApp(params.get('app'));
    const requestedRedirect = params.get('redirect');
    const resolvedApp =
      requestedApp ??
      (requestedRedirect ? resolveProtectedAppFromPath(requestedRedirect) : resolveProtectedAppFromPath(window.location.pathname));
    const normalizedRedirect = normalizeDevLoginRedirect(requestedRedirect, resolvedApp);
    return {
      app: resolvedApp,
      redirect: normalizedRedirect || getDefaultAppPath(resolvedApp),
    };
  }, []);

  useEffect(() => {
    if (!isDevLoginMode) {
      window.location.replace(buildUnauthorizedUrl('INVALID_USER', { app }));
    }
  }, [app]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = cea.trim();
    if (!trimmed) {
      setError('Please enter your CEA number');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const normalizedCea = trimmed.toUpperCase();
      await api.devLogin(normalizedCea, app);
      setDevCea(normalizedCea);
      window.location.replace(redirect);
    } catch (err: any) {
      const errorCode = typeof err?.code === 'string' ? err.code : undefined;
      if (
        errorCode === 'INVALID_USER' ||
        errorCode === 'NO_SUPER_PICKER_ACCESS' ||
        errorCode === 'NO_SUPER_CALCULATOR_ACCESS' ||
        errorCode === 'NO_SUPER_CONNECTOR_ACCESS' ||
        errorCode === 'NO_PROJECT_ESSENTIAL_ACCESS'
      ) {
        window.location.replace(buildUnauthorizedUrl(errorCode, { app }));
        return;
      }
      setLoading(false);
      setError(err?.message || 'Login failed. Please check your CEA number and try again.');
    }
  };

  if (!isDevLoginMode) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: '#f5f5f5',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
          mx: 2,
        }}
      >
        <Typography variant="h5" fontWeight={600} gutterBottom textAlign="center">
          Dev Login
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
          Redirecting to: <strong>{redirect}</strong>
        </Typography>

        <form onSubmit={handleSubmit}>
          <TextField
            label="CEA Number"
            placeholder="e.g. R012345A"
            value={cea}
            onChange={(e) => {
              setCea(e.target.value);
              if (error) setError(null);
            }}
            fullWidth
            autoFocus
            disabled={loading}
            sx={{ mb: 2 }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading || !cea.trim()}
            size="large"
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
}
